import {createSlice, isAnyOf, type SliceSelectors} from '@reduxjs/toolkit'

import {eq} from '@shared/helpers/lang'
import {mapValueKeys, reduceValueKeys} from '@shared/helpers/object'

import {getDeliveryAddressFee} from '@web/actions/deliveryInfo'
import {
    selectProductItemCoupons,
    updateSelectCoupon,
    cancelSelectCoupon,
    selectProductCoupon,
    selectDeliveryCoupon,
    startSimulateCoupon,
    selectProductCouponPage,
    selectDeliveryCouponPage,
    selectStoreCouponPage,
    applySimulateCouponPage,
    getSelectProductDiscount,
    DiscountItem,
    updateCouponDiscount,
    cancelSimulateCouponPage,
    getCoupons,
    removeCouponPageBlock,
} from '@web/actions/productCoupon'
import {restoreSavedState, restoreState} from '@web/actions/restoreState'
import {APIStatus} from '@web/core/constants'
import {
    OrderSheetCoupon,
    OrderSheetImmediateCoupon,
    selectStoreCoupon,
    updateSelectedStoreCoupons,
    OrderSheetCouponState,
    getInitialState,
    getSelectedCoupon,
    convertDiscountInfo,
    eqDiscountItem,
    eqDiscountItemByImmediateCoupon,
    filterProductCoupon,
} from '@web/slices/orderSheetCoupon/slice'

import {OrderSheetCouponReducers, MakeCreateSliceOptions} from './interfaces'

export default (options?: MakeCreateSliceOptions) =>
    createSlice<
        OrderSheetCouponState,
        OrderSheetCouponReducers,
        'orderSheetCoupon',
        SliceSelectors<OrderSheetCouponState>
    >({
        name: 'orderSheetCoupon',
        initialState: getInitialState(),
        reducers: {},
        extraReducers: (builder) => {
            builder
                .addCase(selectProductCoupon, (state, {payload}) => {
                    if (state.apiStatus !== APIStatus.FETCHING) {
                        state.beforeApplyCoupon.productCoupon = {
                            ...state.selected.productCoupon,
                            [payload.itemId]: (state.product.couponsByOrderSheetItemId[payload.itemId] ?? []).filter(
                                ({couponId}) => {
                                    return payload.couponIds.includes(couponId)
                                },
                            ),
                        }
                        state.beforeApplyCoupon.immediateCoupon = {
                            ...state.selected.immediateCoupon,
                            [payload.itemId]: (
                                state.product.immediateCouponsByOrderSheetItemId[payload.itemId] ?? []
                            ).filter(({policyNo}) => {
                                return payload.policyNos.includes(policyNo)
                            }),
                        }
                        state.beforeApplyCoupon.duplicateCoupon = {
                            ...state.selected.duplicateCoupon,
                            [payload.itemId]: (
                                state.product.duplicateCouponsByOrderSheetItemId[payload.itemId] ?? []
                            ).filter(({couponId}) => {
                                return payload.couponIdsByDuplicate.includes(couponId)
                            }),
                        }
                        state.beforeApplyCoupon.deliveryCoupon = state.selected.deliveryCoupon
                        state.beforeApplyCoupon.storeCoupon = state.selected.storeCoupon
                        if (payload.isSelectedPromotionGroupExist) {
                            return
                        }
                        if (payload.isRemove) {
                            state.selected.productCoupon = state.beforeApplyCoupon.productCoupon
                            state.selected.immediateCoupon = state.beforeApplyCoupon.immediateCoupon
                            state.selected.duplicateCoupon = state.beforeApplyCoupon.duplicateCoupon
                        }
                        state.apiStatus = APIStatus.FETCHING
                    }
                })
                .addCase(selectDeliveryCoupon, (state, {payload}) => {
                    if (payload.couponId) {
                        state.selected.deliveryCoupon[payload.deliveryGroupKey] =
                            (state.deliveryFee.couponsByDeliveryGroupKey[payload.deliveryGroupKey] ?? []).find(
                                ({couponId}) => {
                                    return payload.couponId === couponId
                                },
                            ) ?? null
                    } else {
                        state.selected.deliveryCoupon[payload.deliveryGroupKey] = null
                    }
                })
                .addCase(selectStoreCoupon, (state, {payload}) => {
                    if (state.apiStatus !== APIStatus.FETCHING) {
                        state.beforeApplyCoupon.storeCoupon = {
                            ...state.selected.storeCoupon,
                            [payload.merchantNo]: payload.couponId
                                ? ((state.product.couponsByMerchantNo[payload.merchantNo] ?? []).find(({couponId}) => {
                                      return payload.couponId === couponId
                                  }) ?? null)
                                : null,
                        }
                        state.beforeApplyCoupon.productCoupon = state.selected.productCoupon
                        state.beforeApplyCoupon.immediateCoupon = state.selected.immediateCoupon
                        state.beforeApplyCoupon.duplicateCoupon = state.selected.duplicateCoupon
                        state.beforeApplyCoupon.deliveryCoupon = state.selected.deliveryCoupon
                        if (payload.isRemove) {
                            state.selected.storeCoupon = state.beforeApplyCoupon.storeCoupon
                        }
                        state.apiStatus = APIStatus.FETCHING
                    }
                })
                .addCase(updateSelectedStoreCoupons, (state, {payload}) => {
                    state.beforeApplyCoupon.storeCoupon = payload
                    state.selected.storeCoupon = payload
                })
                .addCase(getDeliveryAddressFee.fulfilled, (state, {payload}) => {
                    if (payload.changeDeliveryAreaExtraFeeMessage) {
                        state.beforeApplyCoupon.deliveryCoupon = {}
                        state.selected.deliveryCoupon = state.optimizedCoupon.deliveryCoupon
                    }
                })
                .addCase(updateSelectCoupon, (state) => {
                    state.apiStatus = APIStatus.RESOLVED
                })
                .addCase(cancelSelectCoupon, (state) => {
                    state.apiStatus = APIStatus.REJECTED
                })
                .addCase(selectProductItemCoupons.fulfilled, (state, {payload}) => {
                    state.selected.productCoupon = {
                        ...state.selected.productCoupon,
                        [payload.itemId]: (state.product.couponsByOrderSheetItemId[payload.itemId] ?? []).filter(
                            ({couponId}) => {
                                return payload.selectProductCoupons.some((coupon) => coupon.couponId === couponId)
                            },
                        ),
                    }
                    state.selected.duplicateCoupon = {
                        ...state.selected.duplicateCoupon,
                        [payload.itemId]: (
                            state.product.duplicateCouponsByOrderSheetItemId[payload.itemId] ?? []
                        ).filter(({couponId}) => {
                            return payload.selectDuplicateCoupons.some((coupon) => coupon.couponId === couponId)
                        }),
                    }
                })
                .addCase(getSelectProductDiscount.fulfilled, (state, {payload}) => {
                    const {discountsByItemId, deliveryFeeDiscountsByDeliveryGroupKey, calculatedDiscountFailure} =
                        payload.data
                    const orderSheetItemIdByMerchantNo = payload.orderSheetItemIdByMerchantNo

                    if (deliveryFeeDiscountsByDeliveryGroupKey && !calculatedDiscountFailure) {
                        state.beforeApplyCoupon.deliveryCoupon = state.beforeApplyTemporaryCoupon.deliveryCoupon
                        const DeliveryFeeCoupon = mapValueKeys(
                            (coupon, deliveryGroupKey) => {
                                return deliveryFeeDiscountsByDeliveryGroupKey[deliveryGroupKey]?.discountType ===
                                    'DELIVERY_FEE_DISCOUNT'
                                    ? coupon
                                    : null
                            },
                            eq(state.couponStatus, 'failed')
                                ? state.selected.deliveryCoupon
                                : state.beforeApplyCoupon.deliveryCoupon,
                        )
                        if (eq(state.couponStatus, 'failed')) {
                            state.selected.deliveryCoupon = DeliveryFeeCoupon
                        } else {
                            state.beforeApplyCoupon.deliveryCoupon = DeliveryFeeCoupon
                        }
                        if (state.applyType === 'direct') {
                            state.discount.deliveryItem = mapValueKeys(
                                (discount) => convertDiscountInfo(discount),
                                deliveryFeeDiscountsByDeliveryGroupKey,
                            )
                        } else {
                            state.simulateDiscount.deliveryItem = mapValueKeys(
                                (discount) => convertDiscountInfo(discount),
                                deliveryFeeDiscountsByDeliveryGroupKey,
                            )
                        }
                    }

                    if (discountsByItemId && !calculatedDiscountFailure) {
                        state.beforeApplyCoupon = {
                            productCoupon: state.beforeApplyTemporaryCoupon.productCoupon,
                            duplicateCoupon: state.beforeApplyTemporaryCoupon.duplicateCoupon,
                            immediateCoupon: state.beforeApplyTemporaryCoupon.immediateCoupon,
                            storeCoupon: state.beforeApplyTemporaryCoupon.storeCoupon,
                            deliveryCoupon: state.beforeApplyCoupon.deliveryCoupon,
                            multiPurchaseCoupon: state.beforeApplyTemporaryCoupon.multiPurchaseCoupon,
                        }

                        const ImmediateCoupon = filterProductCoupon<OrderSheetImmediateCoupon, DiscountItem>({
                            selectedCoupons: eq(state.couponStatus, 'failed')
                                ? state.selected.immediateCoupon
                                : state.beforeApplyCoupon.immediateCoupon,
                            couponsByOrderSheetItemId: discountsByItemId,
                            someFunction: eqDiscountItemByImmediateCoupon,
                        })

                        const ProductCoupon = filterProductCoupon<OrderSheetCoupon, DiscountItem>({
                            selectedCoupons: eq(state.couponStatus, 'failed')
                                ? state.selected.productCoupon
                                : state.beforeApplyCoupon.productCoupon,
                            couponsByOrderSheetItemId: discountsByItemId,
                            someFunction: eqDiscountItem,
                        })

                        const DuplicateCoupon = filterProductCoupon<OrderSheetCoupon, DiscountItem>({
                            selectedCoupons: eq(state.couponStatus, 'failed')
                                ? state.selected.duplicateCoupon
                                : state.beforeApplyCoupon.duplicateCoupon,
                            couponsByOrderSheetItemId: discountsByItemId,
                            someFunction: eqDiscountItem,
                        })

                        const StoreCoupon = mapValueKeys(
                            (coupon, merchantNo) => {
                                if (coupon && orderSheetItemIdByMerchantNo[merchantNo]) {
                                    return orderSheetItemIdByMerchantNo[merchantNo].some((orderSheetItemId) => {
                                        return discountsByItemId[orderSheetItemId]?.some(
                                            ({discountType}) => discountType === 'STORE_DISCOUNT',
                                        )
                                    })
                                        ? coupon
                                        : null
                                }
                                return coupon
                            },
                            eq(state.couponStatus, 'failed')
                                ? state.selected.storeCoupon
                                : state.beforeApplyCoupon.storeCoupon,
                        )

                        const MultiPurchaseDiscountTargetItemIds = eq(state.couponStatus, 'failed')
                            ? state.selected.multiPurchaseCoupon
                            : reduceValueKeys<string[], {[orderSheetItemId: string]: DiscountItem[]}>(
                                  (acc, discounts, orderSheetItemId) => {
                                      if (
                                          discounts.some(({discountType}) => discountType === 'MULTI_PURCHASE_DISCOUNT')
                                      ) {
                                          acc.push(String(orderSheetItemId))
                                      }
                                      return acc
                                  },
                                  [],
                                  discountsByItemId,
                              )

                        if (eq(state.couponStatus, 'failed')) {
                            state.selected.immediateCoupon = ImmediateCoupon
                            state.selected.productCoupon = ProductCoupon
                            state.selected.duplicateCoupon = DuplicateCoupon
                            state.selected.storeCoupon = StoreCoupon
                            state.selected.multiPurchaseCoupon = MultiPurchaseDiscountTargetItemIds
                        } else {
                            state.beforeApplyCoupon.immediateCoupon = ImmediateCoupon
                            state.beforeApplyCoupon.productCoupon = ProductCoupon
                            state.beforeApplyCoupon.duplicateCoupon = DuplicateCoupon
                            state.beforeApplyCoupon.storeCoupon = StoreCoupon
                            state.beforeApplyCoupon.multiPurchaseCoupon = MultiPurchaseDiscountTargetItemIds
                        }

                        if (state.applyType === 'direct') {
                            state.discount.productItem = mapValueKeys(
                                (discounts) => discounts.map(convertDiscountInfo),
                                discountsByItemId,
                            )
                        } else {
                            state.simulateDiscount.productItem = mapValueKeys(
                                (discounts) => discounts.map(convertDiscountInfo),
                                discountsByItemId,
                            )
                        }
                    }
                    state.beforeApplyTemporaryCoupon = getSelectedCoupon()
                })
                .addCase(updateCouponDiscount.fulfilled, (state, {payload}) => {
                    const {discountsByItemId, deliveryFeeDiscountsByDeliveryGroupKey, calculatedDiscountFailure} =
                        payload
                    if (discountsByItemId && !calculatedDiscountFailure) {
                        state.selected.immediateCoupon = filterProductCoupon<OrderSheetImmediateCoupon, DiscountItem>({
                            selectedCoupons: state.selected.immediateCoupon,
                            couponsByOrderSheetItemId: discountsByItemId,
                            someFunction: eqDiscountItemByImmediateCoupon,
                        })
                        state.selected.productCoupon = filterProductCoupon<OrderSheetCoupon, DiscountItem>({
                            selectedCoupons: state.selected.productCoupon,
                            couponsByOrderSheetItemId: discountsByItemId,
                            someFunction: eqDiscountItem,
                        })
                        state.selected.duplicateCoupon = filterProductCoupon<OrderSheetCoupon, DiscountItem>({
                            selectedCoupons: state.selected.duplicateCoupon,
                            couponsByOrderSheetItemId: discountsByItemId,
                            someFunction: eqDiscountItem,
                        })
                        state.discount.productItem = mapValueKeys(
                            (discounts) => discounts.map(convertDiscountInfo),
                            discountsByItemId,
                        )
                    }
                    if (deliveryFeeDiscountsByDeliveryGroupKey && !calculatedDiscountFailure) {
                        state.selected.deliveryCoupon = mapValueKeys((coupon, deliveryGroupKey) => {
                            return deliveryFeeDiscountsByDeliveryGroupKey[deliveryGroupKey]?.discountType ===
                                'DELIVERY_FEE_DISCOUNT'
                                ? coupon
                                : null
                        }, state.selected.deliveryCoupon)
                        state.discount.deliveryItem = mapValueKeys(
                            (discount) => convertDiscountInfo(discount),
                            deliveryFeeDiscountsByDeliveryGroupKey,
                        )
                    }
                })
                .addCase(startSimulateCoupon, (state) => {
                    state.beforeApplyCoupon = state.selected
                    state.simulateDiscount = state.discount
                    /**
                     * 통합할인조회 리로드는 카드프로모션/복원 케이스일때만 수행해야함
                     * 통합할인조회를 하지 않고 할인금액이 바뀌는 케이스는 쿠폰모달에서밖에 없음
                     * 따라서 쿠폰모달이 열리면 리로드 상태를 제거한다.
                     */
                    state.reloadStatus = undefined
                    state.applyType = 'simulate'
                })
                .addCase(selectProductCouponPage, (state, {payload}) => {
                    if (state.apiStatus !== APIStatus.FETCHING) {
                        const productCoupons = {
                            ...state.beforeApplyCoupon.productCoupon,
                            [payload.itemId]: (state.product.couponsByOrderSheetItemId[payload.itemId] ?? []).filter(
                                ({couponId}) => {
                                    return payload.couponIds.includes(couponId)
                                },
                            ),
                        }
                        state.beforeApplyTemporaryCoupon.productCoupon = productCoupons

                        const immediateCoupons = {
                            ...state.beforeApplyCoupon.immediateCoupon,
                            [payload.itemId]: (
                                state.product.immediateCouponsByOrderSheetItemId[payload.itemId] ?? []
                            ).filter(({policyNo}) => {
                                return payload.policyNos.includes(policyNo)
                            }),
                        }
                        state.beforeApplyTemporaryCoupon.immediateCoupon = immediateCoupons

                        const duplicateCoupons = {
                            ...state.beforeApplyCoupon.duplicateCoupon,
                            [payload.itemId]: (
                                state.product.duplicateCouponsByOrderSheetItemId[payload.itemId] ?? []
                            ).filter(({couponId}) => {
                                return payload.couponIdsByDuplicate.includes(couponId)
                            }),
                        }
                        state.beforeApplyTemporaryCoupon.duplicateCoupon = duplicateCoupons

                        state.beforeApplyTemporaryCoupon.multiPurchaseCoupon = payload.multiPurchaseDiscounts

                        state.beforeApplyTemporaryCoupon.storeCoupon = state.beforeApplyCoupon.storeCoupon
                        state.beforeApplyTemporaryCoupon.deliveryCoupon = state.beforeApplyCoupon.deliveryCoupon

                        state.apiStatus = APIStatus.FETCHING
                    }
                })
                .addCase(selectDeliveryCouponPage, (state, {payload}) => {
                    if (payload.couponId) {
                        const deliveryCoupon = {
                            ...state.beforeApplyCoupon.deliveryCoupon,
                            [payload.deliveryGroupKey]:
                                (state.deliveryFee.couponsByDeliveryGroupKey[payload.deliveryGroupKey] ?? []).find(
                                    ({couponId}) => couponId === payload.couponId,
                                ) ?? null,
                        }
                        if (!payload.shouldDiscountAmountReload) {
                            state.beforeApplyCoupon.deliveryCoupon = deliveryCoupon
                        }
                        if (payload.shouldDiscountAmountReload && state.apiStatus !== APIStatus.FETCHING) {
                            state.beforeApplyTemporaryCoupon.deliveryCoupon = deliveryCoupon
                            state.beforeApplyTemporaryCoupon.productCoupon = state.beforeApplyCoupon.productCoupon
                            state.beforeApplyTemporaryCoupon.duplicateCoupon = state.beforeApplyCoupon.duplicateCoupon
                            state.beforeApplyTemporaryCoupon.immediateCoupon = state.beforeApplyCoupon.immediateCoupon
                            state.beforeApplyTemporaryCoupon.storeCoupon = state.beforeApplyCoupon.storeCoupon
                            state.beforeApplyTemporaryCoupon.multiPurchaseCoupon =
                                state.beforeApplyCoupon.multiPurchaseCoupon
                            state.apiStatus = APIStatus.FETCHING
                        }
                    } else {
                        state.beforeApplyCoupon.deliveryCoupon[payload.deliveryGroupKey] = null
                    }
                })
                .addCase(selectStoreCouponPage, (state, {payload}) => {
                    if (state.apiStatus !== APIStatus.FETCHING) {
                        const storeCoupons = {
                            ...state.beforeApplyCoupon.storeCoupon,
                            [payload.merchantNo]: payload.couponId
                                ? ((state.product.couponsByMerchantNo[payload.merchantNo] ?? []).find(({couponId}) => {
                                      return payload.couponId === couponId
                                  }) ?? null)
                                : null,
                        }
                        state.beforeApplyTemporaryCoupon.storeCoupon = storeCoupons

                        state.beforeApplyTemporaryCoupon.productCoupon = state.beforeApplyCoupon.productCoupon
                        state.beforeApplyTemporaryCoupon.duplicateCoupon = state.beforeApplyCoupon.duplicateCoupon
                        state.beforeApplyTemporaryCoupon.immediateCoupon = state.beforeApplyCoupon.immediateCoupon
                        state.beforeApplyTemporaryCoupon.deliveryCoupon = state.beforeApplyCoupon.deliveryCoupon
                        state.beforeApplyTemporaryCoupon.multiPurchaseCoupon =
                            state.beforeApplyCoupon.multiPurchaseCoupon

                        state.apiStatus = APIStatus.FETCHING
                    }
                })
                .addCase(applySimulateCouponPage, (state) => {
                    state.selected = state.beforeApplyCoupon
                    state.discount = state.simulateDiscount
                    state.couponPageType = 'default'
                    state.applyType = 'direct'
                })
                .addCase(cancelSimulateCouponPage, (state) => {
                    state.couponPageType = 'default'
                    state.beforeApplyTemporaryCoupon = getSelectedCoupon()
                    state.applyType = 'direct'
                })
                .addCase(getCoupons.rejected, (state) => {
                    /** 혜택계산기 조회 실패해도 배송비는 최적적용으로 적용한다 */
                    state.beforeApplyCoupon.deliveryCoupon = state.optimizedCoupon.deliveryCoupon
                })
                .addCase(removeCouponPageBlock, (state) => {
                    state.couponPageType = 'default'
                })
            options?.extraReducers?.(builder)
            builder
                .addMatcher(isAnyOf(selectProductItemCoupons.fulfilled), (state, {payload}) => {
                    state.discount = payload.discount
                })
                .addMatcher(isAnyOf(restoreSavedState.fulfilled, restoreState), (state, {payload}) => {
                    return payload.orderSheetCoupon ?? state
                })
        },
    })
